import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Reveal, Chapter } from "./Reveal";
import { Send } from "lucide-react";
import { toast } from "sonner";
import { waLink } from "../data/content";
import { PRODUCTS } from "../data/products";
import { useLang, useT } from "../i18n";

const inputCls =
  "w-full border border-slate-700 bg-[#0B0E14] px-4 py-3.5 text-sm text-slate-100 placeholder:text-slate-600 outline-none transition-[border-color,box-shadow] duration-300 focus:border-gold focus:shadow-[0_0_0_1px_rgba(212,175,55,0.4)]";

const Field = ({ label, children }) => (
  <label className="block">
    <span className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">{label}</span>
    {children}
  </label>
);

const EMPTY = {
  name: "", company: "", email: "", whatsapp: "", country: "", product: "",
  quantity: "", spec: "", port: "", shipping: "FOB", payment: "L/C", packaging: "", message: "",
};

const RfqForm = () => {
  const { lang } = useLang();
  const t = useT();
  const [params] = useSearchParams();
  const [form, setForm] = useState(EMPTY);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    const slug = params.get("product");
    const match = PRODUCTS.find((p) => p.slug === slug);
    if (match) setForm((f) => ({ ...f, product: match.name.en }));
  }, [params]);

  const submit = (e) => {
    e.preventDefault();
    const L = t.rfq.msgLabels;
    const lines = [
      t.rfq.msgHeader, "",
      `${L.name}: ${form.name}`,
      form.company && `${L.company}: ${form.company}`,
      form.email && `${L.email}: ${form.email}`,
      form.whatsapp && `${L.whatsapp}: ${form.whatsapp}`,
      form.country && `${L.country}: ${form.country}`,
      form.product && `${L.product}: ${form.product}`,
      form.quantity && `${L.quantity}: ${form.quantity}`,
      form.spec && `${L.spec}: ${form.spec}`,
      form.port && `${L.port}: ${form.port}`,
      `${L.shipping}: ${form.shipping}`,
      `${L.payment}: ${form.payment}`,
      form.packaging && `${L.packaging}: ${form.packaging}`,
      form.message && "", form.message,
    ].filter((l) => l !== false && l !== undefined);
    toast.success(t.rfq.toast);
    window.open(waLink(lines.join("\n")), "_blank", "noopener,noreferrer");
  };

  return (
    <section id="quote" data-testid="rfq-section" className="bg-[#0B0E14] py-24 lg:py-32">
      <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-14 lg:grid-cols-[0.7fr_1.3fr] lg:gap-20">
          <div>
            <Reveal>
              <Chapter number="07" label={t.rfq.label} testid="rfq-chapter" />
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-8 font-display text-3xl font-semibold leading-tight text-white sm:text-4xl">
                {t.rfq.title}
              </h2>
            </Reveal>
            <Reveal delay={0.18}>
              <p className="mt-6 max-w-md text-sm leading-relaxed text-slate-400 md:text-base">{t.rfq.sub}</p>
            </Reveal>
          </div>

          <Reveal delay={0.2}>
            <form onSubmit={submit} data-testid="rfq-form" className="border border-slate-800 bg-[#0B132B] p-7 lg:p-9">
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                <Field label={t.rfq.f.name}>
                  <input data-testid="rfq-name" required value={form.name} onChange={set("name")} className={inputCls} placeholder={t.rfq.ph.name} />
                </Field>
                <Field label={t.rfq.f.company}>
                  <input data-testid="rfq-company" value={form.company} onChange={set("company")} className={inputCls} placeholder={t.rfq.ph.company} />
                </Field>
                <Field label={t.rfq.f.email}>
                  <input data-testid="rfq-email" type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder={t.rfq.ph.email} />
                </Field>
                <Field label={t.rfq.f.whatsapp}>
                  <input data-testid="rfq-whatsapp" value={form.whatsapp} onChange={set("whatsapp")} className={inputCls} placeholder={t.rfq.ph.whatsapp} />
                </Field>
                <Field label={t.rfq.f.country}>
                  <input data-testid="rfq-country" value={form.country} onChange={set("country")} className={inputCls} placeholder={t.rfq.ph.country} />
                </Field>
                <Field label={t.rfq.f.product}>
                  <select data-testid="rfq-product" value={form.product} onChange={set("product")} className={inputCls}>
                    <option value="">—</option>
                    {PRODUCTS.map((p) => (
                      <option key={p.slug} value={p.name.en}>{p.name[lang]}</option>
                    ))}
                    <option value="other">{t.rfq.otherProduct}</option>
                  </select>
                </Field>
                <Field label={t.rfq.f.quantity}>
                  <input data-testid="rfq-quantity" value={form.quantity} onChange={set("quantity")} className={inputCls} placeholder={t.rfq.ph.quantity} />
                </Field>
                <Field label={t.rfq.f.spec}>
                  <input data-testid="rfq-spec" value={form.spec} onChange={set("spec")} className={inputCls} placeholder={t.rfq.ph.spec} />
                </Field>
                <Field label={t.rfq.f.port}>
                  <input data-testid="rfq-port" value={form.port} onChange={set("port")} className={inputCls} placeholder={t.rfq.ph.port} />
                </Field>
                <Field label={t.rfq.f.shipping}>
                  <select data-testid="rfq-shipping" value={form.shipping} onChange={set("shipping")} className={inputCls}>
                    {t.rfq.shippingOptions.map((o) => (
                      <option key={o} value={o}>{o}</option>
                    ))}
                  </select>
                </Field>
                <Field label={t.rfq.f.payment}>
                  <select data-testid="rfq-payment" value={form.payment} onChange={set("payment")} className={inputCls}>
                    {t.rfq.paymentOptions.map((o) => (
                      <option key={o} value={o}>{o}</option>
                    ))}
                  </select>
                </Field>
                <Field label={t.rfq.f.packaging}>
                  <input data-testid="rfq-packaging" value={form.packaging} onChange={set("packaging")} className={inputCls} placeholder={t.rfq.ph.packaging} />
                </Field>
              </div>
              <div className="mt-5">
                <Field label={t.rfq.f.message}>
                  <textarea data-testid="rfq-message" rows={4} value={form.message} onChange={set("message")} className={inputCls} placeholder={t.rfq.ph.message} />
                </Field>
              </div>
              <button
                data-testid="rfq-submit-button"
                type="submit"
                className="mt-7 flex w-full items-center justify-center gap-2 bg-gold px-7 py-4 font-mono text-xs tracking-[0.25em] text-[#0B132B] transition-[box-shadow] duration-300 hover:shadow-[0_0_36px_rgba(212,175,55,0.4)]"
              >
                {t.rfq.submit.toUpperCase()} <Send size={14} />
              </button>
              <p className="mt-4 text-center font-mono text-[10px] tracking-[0.15em] text-slate-600">{t.rfq.note}</p>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default RfqForm;
